import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, PlusCircle } from "lucide-react";
import { type BudgetDurationType, createBudgetBase } from "shared";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAppForm } from "@/hooks/useCreateForm";
import { restrictRoute } from "@/lib/routeBlocker";
import { BudgetTypeInputs } from "./-components/budget-type-inputs";
import { SubBudgetInputs } from "./-components/sub-budgets-input";

export const Route = createFileRoute("/_authed/_org/budgets/create")({
  beforeLoad: async ({ context }) =>
    restrictRoute(context, {
      redirect: {
        to: "/budgets",
      },
      requiredRole: "admin",
    }),
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { queryClient, trpc } = Route.useRouteContext();
  const { mutate, isPending } = useMutation(
    trpc.budget.create.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: ({ name, id }) => {
        toast.success(`Budget created: ${name}`);

        navigate({
          params: {
            groupId: id,
          },
          to: "/budgets/$groupId",
        });
        queryClient.invalidateQueries(trpc.budget.list.queryOptions());
        form.reset();
      },
    }),
  );

  const form = useAppForm({
    defaultValues: {
      description: "" as string | null,
      name: "",
      subBudgets: [{ name: "Other", targetCents: 0 }],
      timing: {
        refreshInterval: "monthly",
        type: "repeating",
      } as BudgetDurationType,
      users: [] as string[],
    },
    onSubmit: ({ value }) => {
      mutate(value);
    },
    onSubmitInvalid: ({ formApi }) => {
      console.dir(formApi.getAllErrors(), {
        depth: null,
      });
    },

    validators: {
      onSubmit: createBudgetBase,
    },
  });

  return (
    <div className="flex h-full flex-col">
      <SheetHeader className="border-b px-6 pt-6 pb-4">
        <SheetTitle>Create a Budget</SheetTitle>
        <SheetDescription>
          Use budgets to track spend for your organization.
        </SheetDescription>
      </SheetHeader>

      <form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-2 pt-6">
          <div className="space-y-4 pb-4">
            <form.AppField
              children={(field) => {
                return <field.TextField label="Name" required />;
              }}
              name="name"
            />

            <form.AppField
              children={(field) => {
                return (
                  <field.TextAreaField label="Description" required={false} />
                );
              }}
              name="description"
            />

            <BudgetTypeInputs
              form={form}
              fields={{
                timing: "timing",
              }}
            />

            <SubBudgetInputs
              form={form}
              fields={{ subBudgets: "subBudgets" }}
            />

            <form.AppField
              children={(field) => (
                <field.UserMultipleSelectField
                  label="Access"
                  descriptionStyle="below"
                  description="These users will be able to spend on this budget. Admins and Bookkeepers can spend on all budgets by default."
                  required
                />
              )}
              name="users"
            />
          </div>
        </div>

        <div className="flex min-h-16 flex-row items-center justify-start gap-3 border-t px-6 py-6">
          <Button
            className="font-semibold"
            disabled={isPending}
            size="default"
            type="submit"
          >
            {isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <PlusCircle className="mr-2 h-4 w-4" />
            )}
            Create
          </Button>
        </div>
      </form>
    </div>
  );
}
